import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Photo, Overlay, OverlayConfig } from '../../types';
import { PhotoCard } from './PhotoCard';
import { PhotoCardSkeleton } from '../UI/Skeleton';
import { PHOTO_GRID_GAP, getPhotoGridCols } from '../shared/PhotoGrid';
import styles from './PhotoGrid.module.css';

interface PhotoGridProps {
  photos: Photo[];
  overlays: Overlay[];
  hasMore: boolean;
  onLoadMore: () => void;
  onPhotoDeleted: (id: string) => void;
  canDeletePhotos?: boolean;
  onHighlightChange?: (id: string, isHighlight: boolean) => void;
  onOpenPhoto?: (photo: Photo, index: number) => void;
  selectMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string, shiftKey?: boolean) => void;
  onEnterSelectMode?: (id: string) => void;
  loadingMore?: boolean;
  defaultOverlayConfig?: OverlayConfig | null;
}

// Grid spec (gap + 3→6 column breakpoints) is shared with the album/My-Photos
// grids via the shared PhotoGrid module so all photo grids stay in lockstep.
const GRID_GAP = PHOTO_GRID_GAP;

export const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  overlays,
  hasMore,
  onLoadMore,
  onPhotoDeleted,
  canDeletePhotos = true,
  onHighlightChange,
  onOpenPhoto,
  selectMode = false,
  selectedIds,
  onToggleSelect,
  onEnterSelectMode,
  loadingMore = false,
  defaultOverlayConfig,
}) => {
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // Start at 0 — useLayoutEffect measures the real width before first paint.
  // ResizeObserver keeps it updated on resize.
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure actual element width synchronously before the first paint so the
  // initial column count is correct (avoids a wrong-column-count flash).
  useLayoutEffect(() => {
    const el = gridContainerRef.current;
    if (el) {
      setContainerWidth(el.getBoundingClientRect().width || el.clientWidth);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) =>
      setContainerWidth(entry.contentRect.width),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const gridCols = getPhotoGridCols(containerWidth);
  const cellSize = Math.floor((containerWidth - (gridCols - 1) * GRID_GAP) / gridCols);
  const rowHeight = cellSize + GRID_GAP;

  const photoRows = useMemo(() => {
    const rows: Photo[][] = [];
    for (let i = 0; i < photos.length; i += gridCols) {
      rows.push(photos.slice(i, i + gridCols));
    }
    return rows;
  }, [photos, gridCols]);

  const scrollMarginRef = useRef(0);
  useLayoutEffect(() => {
    if (gridContainerRef.current) {
      scrollMarginRef.current =
        gridContainerRef.current.getBoundingClientRect().top + window.scrollY;
    }
  });

  const virtualRowCount = photoRows.length + (loadingMore ? 1 : 0);

  const virtualizer = useWindowVirtualizer({
    count: virtualRowCount,
    estimateSize: () => rowHeight,
    overscan: 3,
    scrollMargin: scrollMarginRef.current,
  });

  const loadMoreRef = useRef(onLoadMore);
  useEffect(() => { loadMoreRef.current = onLoadMore; }, [onLoadMore]);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current(); },
      { rootMargin: '300px' },
    );
    observerRef.current.observe(el);
  }, []);

  return (
    <>
      <div
        ref={gridContainerRef}
        className={styles['virtual-grid-wrap']}
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const isSkeletonRow = virtualRow.index === photoRows.length;
          const row = isSkeletonRow ? null : photoRows[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              className={styles['virtual-row-container']}
              style={{
                height: isSkeletonRow
                  ? virtualRow.size
                  : virtualRow.size - GRID_GAP,
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                gridTemplateColumns: `repeat(${gridCols}, ${cellSize}px)`,
                gap: `${GRID_GAP}px`,
              }}
            >
              {isSkeletonRow
                ? Array.from({ length: gridCols }).map((_, i) => (
                    <PhotoCardSkeleton key={i} />
                  ))
                : row!.map((photo, colIdx) => {
                    const photoIdx = virtualRow.index * gridCols + colIdx;
                    return (
                      <PhotoCard
                        key={photo._id}
                        photo={photo}
                        overlays={overlays}
                        onDelete={onPhotoDeleted}
                        canDelete={canDeletePhotos}
                        onHighlightChange={onHighlightChange}
                        onOpen={() => onOpenPhoto?.(photo, photoIdx)}
                        selectMode={selectMode}
                        selected={selectedIds?.has(photo._id) ?? false}
                        onToggleSelect={onToggleSelect}
                        onEnterSelectMode={onEnterSelectMode}
                        defaultOverlayConfig={defaultOverlayConfig}
                        compact
                      />
                    );
                  })}
            </div>
          );
        })}
      </div>
      {hasMore && !loadingMore && (
        <div ref={sentinelCallbackRef} className={styles.sentinel} />
      )}
    </>
  );
};
